import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import type { ITheme, IStyle } from '@fluentui/style-utilities';
import { mergeStyleSets } from '@fluentui/merge-styles';
import ComposeFormRow from './ComposeFormRow';
import { isFeatureEnabled } from 'owa-feature-flags';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import createFindControlViewState from 'owa-recipient-common/lib/utils/createFindControlViewState';
import { IconWithGALForCalendar } from '../../index';
import updateMailTipsForRecipients from 'owa-calendar-compose-form-actions/lib/actions/updateMailTipsForRecipients';
import updateLastFocusedControl from 'owa-addins-editor-plugin/lib/actions/updateLastFocusedControl';
import { LastFocusedControl } from 'owa-editor-addin-plugin-types/lib/utils/AddinViewState';
import { getComposeEvent, getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { AttendeeWell } from 'owa-readwrite-recipient-well-fabric/lib/components/PeoplePickerTypes';
import type ReadWriteFabricRecipientWell from 'owa-readwrite-recipient-well-fabric/lib/components/ReadWriteFabricRecipientWell';
import type AttendeePersonaProps from 'owa-readwrite-recipient-well-fabric/lib/store/schema/AttendeePersonaProps';
import type FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import type ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import RecipientType from 'owa-recipient-types/lib/types/RecipientType';
import {
    convertRecipientsToAttendees,
    convertAttendeesToRecipientWellViewStates,
} from 'owa-calendar-forms-utils/lib/utils/recipientAttendeeConversions';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import type RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { freeBusyCache, FreeBusyType, isConflictFromFreeBusy } from 'owa-freebusy-cache';
import RecipientAvailabilityType from 'owa-readwrite-recipient-well/lib/store/schema/RecipientAvailabilityType';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';

import styles from './AttendeeRow.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface EditableAttendeeRowProps {
    colorScheme: ColorScheme;
    theme: ITheme;
    getWellViewState: () => RecipientWellWithFindControlViewState;
    onAttendeesChanged: (props: AttendeeType[]) => void;
    placeholder: string;
    attendees: AttendeeType[];
    iconName: string;
    formId: string;
    optionalButton?: JSX.Element;
    shouldFocusOnMount?: boolean;
    showAvailability?: boolean;
    iconContainerClassName?: string;
    galAriaLabel?: string;
    recipientType?: RecipientType;
    className?: string;
    wellItemStyles?: IStyle;
}

const EditableAttendeeRow: React.FunctionComponent<EditableAttendeeRowProps> = props => {
    const {
        colorScheme,
        theme,
        getWellViewState,
        onAttendeesChanged,
        placeholder,
        attendees,
        iconName,
        optionalButton,
        shouldFocusOnMount,
        showAvailability,
        iconContainerClassName,
        galAriaLabel,
        recipientType,
        className,
        formId,
        wellItemStyles,
    } = props;
    const event = getComposeEvent(formId);
    const targetWindow = React.useContext(ProjectionContext);

    const recipientWellRef = React.useRef<ReadWriteFabricRecipientWell<AttendeePersonaProps>>();
    /**
     * The wellViewState is temporary until VSO 30418 and VSO 35590 are addressed.
     * It is created here to keep the well in sync with the event when attendees are added from external sources.
     */
    const wellViewState = useComputed(getWellViewState);

    const [hasSetFocus, setHasSetFocus] = React.useState(false);

    const setRecipientWellRef = React.useCallback(
        ref => {
            recipientWellRef.current = ref;
            if (shouldFocusOnMount && !hasSetFocus) {
                targetWindow.requestAnimationFrame(() => {
                    if (recipientWellRef.current) {
                        recipientWellRef.current.focus();
                        setHasSetFocus(true);
                    }
                });
            }
        },
        [shouldFocusOnMount, hasSetFocus, targetWindow]
    );

    const onAttendeePickerHidden = React.useCallback(() => {
        if (recipientWellRef.current) {
            recipientWellRef.current.forceResolveQueryString();
        }
    }, []);
    const onAttendeePicked = React.useCallback(
        (attendee: FindRecipientPersonaType): FindRecipientPersonaType => {
            if (recipientWellRef.current) {
                recipientWellRef.current.clearQueryString();
            }
            return attendee;
        },
        []
    );
    const onRecipientsChanged = React.useCallback(
        (recipients: ReadWriteRecipientViewState[]) => {
            updateMailTipsForRecipients(recipients, true, formId);
            onAttendeesChanged(convertRecipientsToAttendees(recipients));
        },
        [onAttendeesChanged, formId]
    );

    const onControlFocus = React.useCallback(() => {
        // For now only full compose form needs this
        const fullComposeViewState = getFullComposeViewState(formId);
        if (fullComposeViewState) {
            updateLastFocusedControl(fullComposeViewState.addin, LastFocusedControl.Other);
        }
    }, [formId]);

    const borderColorStyle = generateBorderStyle(colorScheme);
    const isGalModalEnabled = isFeatureEnabled('cal-mf-GALRecipientWell');

    const iconElement = isGalModalEnabled ? (
        <IconWithGALForCalendar
            icon={iconName}
            ariaLabel={galAriaLabel}
            recipientType={recipientType}
            updateAttendees={onAttendeesChanged}
            recipients={wellViewState.get().recipients}
        />
    ) : undefined;

    if (showAvailability) {
        const { Start, End, IsAllDayEvent } = event;
        attendees.forEach(attendee => {
            const status = freeBusyCache.getAggregatedFreeBusy(
                attendee.Mailbox.EmailAddress,
                Start,
                End,
                IsAllDayEvent
            );
            const unknownStatus: boolean =
                status === FreeBusyType.NoData || status === FreeBusyType.Loading;
            const freeStatus: boolean = !isConflictFromFreeBusy(status);
            let persona = (attendee as AttendeePersonaProps).persona;
            if (persona) {
                persona.PersonaAvailability = unknownStatus
                    ? RecipientAvailabilityType.Unknown
                    : freeStatus
                    ? RecipientAvailabilityType.Available
                    : RecipientAvailabilityType.Unavailable;
            }
        });
    }

    return (
        <ComposeFormRow
            iconName={!isGalModalEnabled ? iconName : undefined}
            iconElement={iconElement}
            containerClassName={classNames(styles.recipientWell, borderColorStyle.bottomBorder)}
            className={className}
            iconContainerClassName={
                isGalModalEnabled
                    ? styles.galIcon
                    : classNames(styles.iconContainer, iconContainerClassName)
            }>
            <AttendeeWell
                containerClassName={styles.recipientWellContainer}
                wellClassName={styles.wellClassName}
                findControlViewState={createFindControlViewState(
                    event.ParentFolderId.mailboxInfo.userIdentity
                )}
                defaultSelectedItems={attendees.slice() as AttendeePersonaProps[]}
                selectedItems={attendees as AttendeePersonaProps[]}
                draggableItemType={DraggableItemTypes.ReadWriteRecipient}
                recipientType={RecipientType.None}
                onItemsChanged={onRecipientsChanged}
                inputProps={{
                    onFocus: onControlFocus,
                    placeholder: attendees.length == 0 ? placeholder : null,
                    'aria-label': placeholder,
                }}
                theme={theme}
                ref={setRecipientWellRef}
                convertInitialItemsToWellItems={convertAttendeesToRecipientWellViewStates}
                onPickerHidden={onAttendeePickerHidden}
                onItemSelected={onAttendeePicked}
                showAvailability={showAvailability}
                // The ReadWriteFabricRecipientWell well will pre-resolve pending recipients
                // before adding them to the state & calling onItemsChanged.
                shouldPreResolveRecipients={true}
                wellItemStyles={mergeStyleSets(
                    { wellItemBox: [{ margin: '5px 4px' }] },
                    wellItemStyles
                )}
            />
            {optionalButton}
        </ComposeFormRow>
    );
};

export default observer(EditableAttendeeRow);
